// ---- date helpers (compare by local calendar day) -----------------------
function ymd(d) {
  if (!d) return null;
  const dt = (typeof d === 'string') ? new Date(d + 'T00:00:00') : new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}
function todayYmd() {
  const dt = new Date();
  return ymd(dt);
}
function addDaysYmd(ymdStr, n) {
  const [y,m,day] = (ymdStr || '').split('-').map(Number);
  if (!y || !m || !day) return null;
  const dt = new Date(y, m-1, day + n);
  return ymd(dt);
}
function getStayStatus(checkInStr, checkOutStr) {
  const inY = ymd(checkInStr);
  const outY = ymd(checkOutStr);
  const today = todayYmd();
  if (!inY && !outY) return 'status';
  if (inY && today < inY) return 'upcoming check‑in';
  if (outY && today > outY) return 'checked out';
  if (outY && today === outY) return 'checks out today';
  if (outY && addDaysYmd(today, 1) === outY) return 'checks out tomorrow';
  return 'staying';
}
function statusColor(label) {
  switch (label) {
    case 'staying': return 'info';
    case 'checks out today': return 'warning';
    case 'checks out tomorrow': return 'secondary';
    case 'checked out': return 'default';
    case 'upcoming check‑in': return 'success';
    default: return 'default';
  }
}
// ------------------------------------------------------------------------
import React from 'react';
import {
  Drawer, Box, Typography, Stack, Chip, Avatar, IconButton, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getGuestProfile, getCheckinLogs } from '../../api/conciergeApi';

function prettyList(arr) { return Array.isArray(arr) && arr.length ? arr.join(', ') : '—'; }

function resolveDatesFromLog(l) {
  const bk = l?.result?.booking || {};
  const rb = l?.requestBody || {};
  return {
    checkIn: bk.checkIn || rb.checkIn || null,
    checkOut: bk.checkOut || rb.checkOut || null,
    phone: rb.phone || (l?.result?.guest?.phone ?? null),
    name: rb.fullName || (l?.result?.guest?.fullName ?? null),
  };
}

export default function GuestProfileDrawer({
  open,
  onClose,
  propertyId,
  userUid,
  fallbackName,     // optional: name shown while loading / if no profile name
  fallbackEmail,    // optional
}) {
  const [loading, setLoading] = React.useState(false);
  const [guest, setGuest] = React.useState(null);
  const [logs, setLogs] = React.useState([]);
  const [roomNumber, setRoomNumber] = React.useState(null);

  const name = guest?.profile?.identity?.fullName || fallbackName || 'Guest';
  const email = guest?.email || fallbackEmail || '—';
  const initials = (name || 'G').trim().slice(0, 1).toUpperCase();

  const load = React.useCallback(async () => {
    if (!open || !propertyId || !userUid) return;
    setLoading(true);
    console.log('[GuestProfileDrawer] load()', { open, propertyId, userUid });
    try {
      const [gp, cl] = await Promise.all([
        getGuestProfile({ propertyId, userUid }),
        getCheckinLogs({ propertyId, userUid, limit: 20 }),
      ]);
      console.log('[GuestProfileDrawer] raw guest profile', gp);
      console.log('[GuestProfileDrawer] raw checkin logs', cl);
      setGuest(gp);
      // Accept different API shapes: array | {logs} | {data} | {items}
      let parsedLogs = [];
      if (Array.isArray(cl)) {
        parsedLogs = cl;
      } else if (cl && Array.isArray(cl.logs)) {
        parsedLogs = cl.logs;
      } else if (cl && Array.isArray(cl.data)) {
        parsedLogs = cl.data;
      } else if (cl && Array.isArray(cl.items)) {
        parsedLogs = cl.items;
      }
      console.log('[GuestProfileDrawer] parsed logs len=', parsedLogs.length, parsedLogs.slice(0, 2));
      setLogs(parsedLogs);

      const fromProfile = gp?.roomNumber || gp?.booking?.roomNumber || gp?.guest?.roomNumber;
      if (fromProfile) {
        setRoomNumber(String(fromProfile));
      } else {
        const withRoom = parsedLogs.find((l) => l?.roomNumber || l?.result?.booking?.roomNumber || l?.requestBody?.roomNumber);
        const candidate = withRoom?.roomNumber || withRoom?.result?.booking?.roomNumber || withRoom?.requestBody?.roomNumber;
        setRoomNumber(candidate ? String(candidate) : null);
      }
    } catch (e) {
      console.warn('[GuestProfileDrawer] load error', e);
      if (e && e.response) {
        console.warn('[GuestProfileDrawer] error response', e.response);
      }
      setGuest(null);
      setLogs([]);
      setRoomNumber(null);
    } finally {
      setLoading(false);
    }
  }, [open, propertyId, userUid]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, maxWidth: '100vw', p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar sx={{ width: 36, height: 36 }}>{initials}</Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1 }}>{name}</Typography>
              <Typography variant="body2" color="text.secondary">{email}</Typography>
              {roomNumber ? (
                <Chip size="small" label={`Room ${roomNumber}`} sx={{ mt: 0.75, fontWeight: 600 }} />
              ) : null}
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {loading ? (
          <Typography variant="body2">Loading…</Typography>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Contact</Typography>
            <Typography variant="body2">
              <strong>Phone:</strong> {guest?.profile?.contact?.phone || '—'}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Preferences</Typography>
            <Typography variant="body2"><strong>Dining:</strong> {prettyList(guest?.stayPreferences?.diningPrefs)}</Typography>
            <Typography variant="body2"><strong>Interests:</strong> {prettyList(guest?.stayPreferences?.interestPrefs)}</Typography>
            <Typography variant="body2"><strong>Room:</strong> {prettyList(guest?.stayPreferences?.roomPrefs)}</Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Recent check-ins</Typography>
            {logs.length === 0 ? (
              <Typography variant="body2">No logs.</Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {logs.map((l) => {
                  const { checkIn, checkOut, phone, name } = resolveDatesFromLog(l);
                  return (
                    <Box key={String(l._id)} sx={{ border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={l.source} />
                        {(() => {
                          const { checkIn, checkOut } = resolveDatesFromLog(l);
                          const label = getStayStatus(checkIn, checkOut);
                          return <Chip size="small" label={label} color={statusColor(label)} />;
                        })()}
                        <Typography variant="caption" color="text.secondary">
                          {l.createdAt ? new Date(l.createdAt).toLocaleString() : ''}
                        </Typography>
                      </Stack>
                      {name && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Name: {name}
                        </Typography>
                      )}
                      {checkIn && (
                        <Typography variant="body2">Check-in: {checkIn}</Typography>
                      )}
                      {checkOut && (
                        <Typography variant="body2">Check-out: {checkOut}</Typography>
                      )}
                      {phone && (
                        <Typography variant="body2">Phone: {phone}</Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}
